// The form that walks through the SSO door, and what to say when it comes back.
//
// The journey has not changed: a buyer claims what they bought from a
// marketplace and signs in with an email and a password. Single sign-on is
// added later, per customer, by support — and metricsIQ's identity provider is
// a BROKER, routing an inbound sign-in onward to that customer's own IdP by
// email domain. So the app never picks a provider and never holds a list of
// them — and it no longer asks the service which door an address gets either.
// The screen draws both, so there is no routing decision on this side to make,
// and nothing here reads the address to decide anything (Owner ruling,
// 2026-09-02).
//
// Nothing here holds a token. The federated round trip comes back to the
// backend, which sets the refresh cookie and redirects into the portal — and
// the app's ordinary boot renewal turns that cookie into a session, through the
// same door a reload uses. See backend/portal_auth/federation.py.

import { csrfToken } from './csrf';

/**
 * Where the SSO button posts to.
 *
 * A real form navigation, not a fetch: the browser has to LEAVE for the
 * identity provider, and a fetch cannot take it there.
 */
export const FEDERATED_START_URL = '/auth/federated/start/';

/**
 * The hidden fields the SSO form must carry.
 *
 * The CSRF token because starting a login is a credential-shaped act and the
 * backend refuses one without it; `keep_signed_in` because the same checkbox
 * governs a federated session's cookie as governs a password one's, and it
 * rides in the server's session across the round trip rather than in a URL the
 * person could edit.
 *
 * `email` is a HINT and never a gate. Where somebody has typed one it travels
 * to the provider as OIDC `login_hint`, so they are not asked for the same
 * address twice; the button works without it, because the provider asks for the
 * address itself when it arrives without one. Left out entirely when the box is
 * empty rather than sent blank, so a visitor who typed nothing makes exactly
 * the request this door has always made.
 */
export function federatedStartFields(
  keepSignedIn: boolean,
  email = '',
): Record<string, string> {
  const fields: Record<string, string> = {
    csrfmiddlewaretoken: csrfToken(),
    keep_signed_in: keepSignedIn ? 'true' : 'false',
  };

  if (email.trim()) {
    fields.email = email.trim();
  }

  return fields;
}

/**
 * What the sign-in screen says about a federated round trip that came back
 * refused, keyed by the token the backend redirected with.
 *
 * Worded here rather than sent by the service because these are the only
 * refusals in the portal a person meets AFTER proving who they are — the
 * provider vouched for them — so there is no oracle to protect and every
 * reason to say which of the two questions failed. A blank bounce to this
 * screen is what the person would otherwise get, and it reads as a bug.
 */
export const FEDERATED_REFUSALS: Record<string, string> = {
  // Worded for both audiences because the refusal it reports is about both:
  // the service asked whether this person acts for a customer OR across the
  // fleet, and neither answered. Naming the second way in gives nothing away
  // that the first does not — the reader has already proved who they are at
  // metricsIQ's own broker, and what P-9 protects is the fleet ADDRESSES,
  // which go on answering 404 to anybody without the role.
  'not-entitled':
    'Your identity provider signed you in, but that account is not entitled ' +
    'to anything here yet — neither a company of its own nor a metricsIQ ' +
    'role. Access comes from claiming a subscription, from being invited by ' +
    'somebody who already has one, or from being granted the fleet role. ' +
    'Ask whoever set that up, or contact support.',
  // The identity provider itself refused, before this service was ever asked a
  // question about the person. It sends a reason alongside that refusal, and
  // this sentence deliberately does not repeat it: Owner ruling of 2026-09-02,
  // on seeing Okta's "User is not assigned to the client application." drawn
  // on a page. A reason like that one tells whoever provoked the refusal that
  // this application exists, that the address is known to the provider, and
  // what would have to change — and whoever provoked it need not be the person
  // who owns the address. It is the same disclosure the sign-in card itself
  // withholds by drawing both doors to everybody and reading the address to
  // decide nothing, so withholding it here is that rule held one step later,
  // not a new one. The provider's words go to the service's log instead,
  // where an operator can read them and a stranger cannot; the backend end of
  // this is commerce/identity_refusals.py. The second sentence is the part
  // that is useful and gives nothing away: it names a role, and every reader
  // already knows who theirs is.
  'provider-refused':
    'Your identity provider did not sign you in. If you expected access, ask ' +
    'whoever administers single sign-on for your organisation.',
  incomplete:
    'That sign-in did not finish. Start again from your work email below.',
  unavailable:
    'Single sign-on is not configured on this deployment. Sign in with your ' +
    'password, or contact support.',
};
